/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Activity, Bot } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Step 1 of the BookingWizard: triage information (symptoms, allergies, meds)
 * and shortcut to open the AI Triage chatbot.
 *
 * Extracted from BookingWizard.jsx (Phase 8 refactor).
 */
const StepTriage = ({
  triage,
  onTriageChange,
  reason,
  onReasonChange,
  aiTriageFinal,
  onOpenAiTriage,
}) => {
  const { t } = useTranslation();

  const setField = (key, value) => onTriageChange({ ...triage, [key]: value });

  return (
    <div className="space-y-4 animate-fade-slide">
      <div className="rounded-2xl bg-accent/5 border border-accent/20 p-3 flex items-start gap-2">
        <Activity className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground">{t('triageIntro')}</p>
      </div>

      {/* AI Triage conversational shortcut */}
      <div className="rounded-2xl border border-primary/30 bg-primary-gradient/[0.04] p-3 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Bot className="h-4 w-4 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Triage guidato dall'AI</div>
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Rispondi ad alcune domande e ti prepareremo una sintesi per il medico.
          </p>
          {aiTriageFinal && (
            <div
              className="mt-1.5 flex items-center gap-1.5 flex-wrap text-[11px]"
              data-testid="ai-triage-summary-inline"
            >
              <span
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold',
                  aiTriageFinal.urgency === 'high'
                    ? 'bg-red-100 text-red-800 border-red-200'
                    : aiTriageFinal.urgency === 'medium'
                    ? 'bg-amber-100 text-amber-800 border-amber-200'
                    : 'bg-emerald-100 text-emerald-800 border-emerald-200',
                )}
              >
                Urgenza:{' '}
                {aiTriageFinal.urgency === 'high'
                  ? 'Alta'
                  : aiTriageFinal.urgency === 'medium'
                  ? 'Media'
                  : 'Bassa'}
              </span>
              {aiTriageFinal.red_flags?.length > 0 && (
                <span className="text-red-600 font-medium">
                  {aiTriageFinal.red_flags.length} red flag
                </span>
              )}
            </div>
          )}
        </div>
        <Button
          variant={aiTriageFinal ? 'outline' : 'default'}
          size="sm"
          onClick={onOpenAiTriage}
          data-testid="ai-triage-open-btn"
        >
          <Bot className="h-3.5 w-3.5" /> {aiTriageFinal ? 'Modifica' : 'Avvia'}
        </Button>
      </div>

      <div>
        <Label htmlFor="triage-symptoms">{t('triageSymptoms')} *</Label>
        <Textarea
          id="triage-symptoms"
          rows={3}
          maxLength={1000}
          placeholder={t('triageSymptomsPh')}
          value={triage.symptoms}
          onChange={(e) => setField('symptoms', e.target.value)}
          data-testid="triage-symptoms-input"
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="triage-duration">{t('triageDuration')}</Label>
          <Input
            id="triage-duration"
            placeholder={t('triageDurationPh')}
            value={triage.duration}
            onChange={(e) => setField('duration', e.target.value)}
            data-testid="triage-duration-input"
          />
        </div>
        <div>
          <Label htmlFor="triage-severity">
            {t('triageSeverity')}:{' '}
            <span className="font-bold text-primary">{triage.severity}/10</span>
          </Label>
          <input
            id="triage-severity"
            type="range"
            min={1}
            max={10}
            value={triage.severity}
            onChange={(e) => setField('severity', Number(e.target.value))}
            className="w-full accent-primary mt-3"
            data-testid="triage-severity-input"
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="triage-allergies">{t('triageAllergies')}</Label>
          <Input
            id="triage-allergies"
            placeholder={t('triageAllergiesPh')}
            value={triage.allergies}
            onChange={(e) => setField('allergies', e.target.value)}
            data-testid="triage-allergies-input"
          />
        </div>
        <div>
          <Label htmlFor="triage-meds">{t('triageCurrentMeds')}</Label>
          <Input
            id="triage-meds"
            placeholder={t('triageCurrentMedsPh')}
            value={triage.current_meds}
            onChange={(e) => setField('current_meds', e.target.value)}
            data-testid="triage-meds-input"
          />
        </div>
      </div>
      <div>
        <Label htmlFor="triage-reason">{t('telemedReasonOptional')}</Label>
        <Textarea
          id="triage-reason"
          rows={2}
          maxLength={500}
          placeholder={t('telemedReasonPh')}
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          data-testid="booking-reason-input"
        />
      </div>
    </div>
  );
};

export default StepTriage;
